/** Mirrors the JSON shapes returned by the backend API. */

export type EntryStatus =
  | 'pending'
  | 'confirmed'
  | 'waitlisted'
  | 'withdrawn'
  | 'dns'
  | 'dnf'
  | 'dsq';

export type PaymentStatus = 'unpaid' | 'paid' | 'refunded' | 'waived';

export type Gender = 'female' | 'male' | 'other' | 'prefer_not_to_say';

export type SeedingMode = 'lanes' | 'waves';

export interface PublicCategory {
  id: string;
  label: string;
}

export interface PublicEvent {
  eventId: string;
  name: string;
  tagline: string;
  description: string;
  eventDate: string;
  startTime: string;
  timezone: string;
  venue: string;
  venueAddress: string;
  distanceMeters: number;
  seedingMode: SeedingMode;
  entryFeePence: number;
  currency: string;
  minAge: number;
  categories: PublicCategory[];
  minSeedTimeMs: number;
  maxSeedTimeMs: number;
  requireMedicalDeclaration: boolean;
  contactEmail: string;
  registrationOpensAt: string;
  registrationClosesAt: string;
  registrationOpen: boolean;
  placesRemaining: number;
  heatsPublished: boolean;
  heatsPublishedAt: string | null;
}

export interface EventCategory extends PublicCategory {
  seedSeparately: boolean;
}

/** Full event config, admin only. */
export interface AdminEvent {
  eventId: string;
  name: string;
  tagline: string;
  description: string;
  eventDate: string;
  startTime: string;
  timezone: string;
  venue: string;
  venueAddress: string;
  distanceMeters: number;
  laneCount: number;
  seedingMode: SeedingMode;
  /** Fallback heat duration, used when a heat has no usable predicted times. */
  heatDurationMinutes: number;
  /** Pool clearing time between heats. */
  changeoverMinutes: number;
  maxEntries: number;
  minAge: number;
  entryFeePence: number;
  currency: string;
  registrationOpensAt: string;
  registrationClosesAt: string;
  contactEmail: string;
  categories: EventCategory[];
  /** Free text shown to competitors telling them where to send the entry fee. */
  paymentMessage: string;
  minSeedTimeMs: number;
  maxSeedTimeMs: number;
  requireMedicalDeclaration: boolean;
  heatsGeneratedAt: string | null;
  heatsPublishedAt: string | null;
  heatsLocked: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Entry {
  entryId: string;
  eventId: string;
  reference: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  gender: Gender;
  club: string;
  categoryId: string;
  seedTimeMs: number;
  seedTimeFormatted: string;
  ageGroup: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  medicalNotes: string;
  status: EntryStatus;
  paymentStatus: PaymentStatus;
  paymentReference: string;
  adminNotes: string;
  consentRules: boolean;
  consentMedia: boolean;
  createdAt: string;
  updatedAt: string;
  lastEditedBy: string | null;
}

/** What a competitor sees about their own entry (no admin notes). */
export type OwnEntry = Omit<Entry, 'adminNotes' | 'lastEditedBy'>;

/**
 * Where to send the entry fee, built by the API so the confirmation screen and
 * "Find my entry" cannot drift apart. `instructions` is newline separated and
 * must be rendered with `white-space: pre-line`.
 */
export interface PaymentInstructions {
  amountPence: number;
  currency: string;
  instructions: string;
}

export interface LookupResult {
  entry: OwnEntry;
  assignment: HeatAssignment | null;
  /** Null once the entry is paid, refunded, waived or withdrawn. */
  payment: PaymentInstructions | null;
}

export interface HeatAssignment {
  heatNumber: number;
  heatName: string;
  lane: number;
  scheduledAt: string | null;
}

export interface StartListLane {
  lane: number;
  reference: string;
  firstName: string;
  lastName: string;
  club: string;
  ageGroup: string;
  categoryId: string;
  seedTimeFormatted: string;
}

export interface StartListHeat {
  heatNumber: number;
  name: string;
  groupId: string;
  groupLabel: string;
  scheduledAt: string | null;
  lanes: StartListLane[];
}

export interface StartLists {
  publishedAt: string;
  seedingMode: SeedingMode;
  heats: StartListHeat[];
}

export interface AdminHeatLane extends StartListLane {
  entryId: string;
  seedTimeMs: number;
}

export interface AdminHeat {
  eventId: string;
  heatNumber: number;
  name: string;
  groupId: string;
  groupLabel: string;
  scheduledAt: string | null;
  /** Always filled by the API, falling back to the event default. */
  durationMinutes: number;
  lanes: AdminHeatLane[];
  createdAt: string;
}

export interface HeatSetMeta {
  eventId: string;
  generatedAt: string;
  generatedBy: string | null;
  publishedAt: string | null;
  locked: boolean;
  heatCount: number;
  swimmerCount: number;
  params: {
    seedingMode: SeedingMode;
    laneCount: number;
    balanceHeats: boolean;
    includeStatuses: EntryStatus[];
    groupByCategory: boolean;
    /** Scheduling settings the set was generated with. Absent on older sets. */
    heatDurationMinutes?: number;
    changeoverMinutes?: number;
  };
}

export interface HeatsResponse {
  heats: AdminHeat[];
  meta: HeatSetMeta | null;
  published: boolean;
  publishedAt: string | null;
  locked: boolean;
  seedingMode: SeedingMode;
}

export interface EventStats {
  total: number;
  active: number;
  capacity: number;
  placesRemaining: number;
  byStatus: Record<string, number>;
  byPaymentStatus: Record<string, number>;
  byCategory: Record<string, number>;
  byAgeGroup: Record<string, number>;
  byGender: Record<string, number>;
  revenuePence: { collected: number; outstanding: number };
  seedTime: { fastestMs: number | null; slowestMs: number | null; medianMs: number | null };
  registrationsByDay: { date: string; count: number }[];
}

export interface Pagination {
  total: number;
  totalUnfiltered: number;
  offset: number;
  limit: number;
}

export interface ImportPlanRow {
  /** Line number in the uploaded file, counting the header row. */
  line: number;
  action: 'create' | 'update' | 'error' | 'skipped';
  name: string;
  email: string;
  reference: string | null;
  seedTime: string;
  entryId: string | null;
  errors: string[];
  /** Repairs and assumptions applied, so nothing happens silently. */
  notes: string[];
}

export interface ImportPlan {
  dryRun: boolean;
  rows: ImportPlanRow[];
  ignoredHeaders: string[];
  summary: {
    total: number;
    create: number;
    update: number;
    errors: number;
    /** Template example rows, recognised and left alone. */
    skipped: number;
  };
  capacity: { active: number; max: number; afterImport: number; exceeds: boolean };
  heatsStale: boolean;
  /** Present only on a committed import. */
  created?: number;
  updated?: number;
  failures?: { line: number; message: string }[];
}

/** A member of the Cognito `admins` group, i.e. someone with portal access. */
export interface Organiser {
  /** Cognito username (a UUID, since the pool signs in by email). */
  username: string;
  email: string;
  /** FORCE_CHANGE_PASSWORD while an invitation is outstanding, then CONFIRMED. */
  status: string;
  enabled: boolean;
  createdAt: string | null;
  /** null when the MFA state could not be read, so it is not shown as "off". */
  mfaEnabled: boolean | null;
}

export const ENTRY_STATUS_OPTIONS: { value: EntryStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'waitlisted', label: 'Waitlisted' },
  { value: 'withdrawn', label: 'Withdrawn' },
  { value: 'dns', label: 'Did not start' },
  { value: 'dnf', label: 'Did not finish' },
  { value: 'dsq', label: 'Disqualified' },
];

export const PAYMENT_STATUS_OPTIONS: { value: PaymentStatus; label: string }[] = [
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'paid', label: 'Paid' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'waived', label: 'Waived' },
];

export const GENDER_OPTIONS: { value: Gender; label: string }[] = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];
